import { NavLink, Outlet } from "react-router-dom";

import { cn } from "@/lib/utils";

/**
 * Layout for the `/presentations` parent route (issue #606). Renders a
 * header and a sub-nav (Pending / Decided) above the active sub-page's
 * `<Outlet />`.
 *
 * Two tabs:
 *
 * - **Pending** — `/presentations` (index). The current
 *   `pending_review` queue landed by #575 item 1.
 * - **Decided** — `/presentations/decided` (issue #606). Past
 *   decisions the operator can browse back to for consistency,
 *   auditability, and handoff.
 *
 * Mirrors {@link CatalogueLayout} — same shape, same `SubNavTab`
 * treatment. The header (`Reviews` eyebrow + `Presentations` title)
 * stays fixed across both subtabs so a tab switch feels like a tab
 * change, not a full navigation.
 */
export function PresentationsLayout() {
  return (
    <div className="space-y-6">
      <div>
        <p className="label-mono">Reviews</p>
        <h1 className="font-display text-4xl text-text">Presentations</h1>
      </div>
      <nav className="flex items-center gap-1 border-b border-border">
        <SubNavTab to="" end>
          Pending
        </SubNavTab>
        <SubNavTab to="decided">Decided</SubNavTab>
      </nav>
      <Outlet />
    </div>
  );
}

function SubNavTab({
  to,
  children,
  end,
}: {
  to: string;
  children: React.ReactNode;
  /** Mark this link as `end` so `/presentations/decided` doesn't keep
   *  the Pending tab active. Mirrors react-router's nav pattern. */
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "px-4 py-2 -mb-px text-sm font-mono uppercase tracking-label transition-colors",
          isActive
            ? "text-accent border-b-2 border-accent"
            : "text-text-muted hover:text-text border-b-2 border-transparent",
        )
      }
    >
      {children}
    </NavLink>
  );
}
